import type * as CSS from 'csstype'
import { system, Config } from '@styled-system/core'
import { ResponsiveValue, TLength } from '../utils/types'

export interface GridProps {
  /**
   * The grid-area CSS property is a shorthand property for grid-row-start, grid-column-start, grid-row-end
   * and grid-column-end, specifying a grid item’s size and location within the grid row by contributing a line,
   * a span, or nothing (automatic) to its grid placement, thereby specifying the edges of its grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-area)
   */
  gridArea?: ResponsiveValue<CSS.GridAreaProperty>
  /**
   * The grid-auto-columns CSS property specifies the size of an implicitly-created grid column track.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-columns)
   */
  gridAutoColumns?: ResponsiveValue<CSS.GridAutoColumnsProperty<TLength>>
  /**
   * The grid-auto-flow CSS property controls how the auto-placement algorithm works, specifying exactly
   * how auto-placed items get flowed into the grid.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-flow)
   */
  gridAutoFlow?: ResponsiveValue<CSS.GridAutoFlowProperty>
  /**
   * The grid-auto-rows CSS property specifies the size of an implicitly-created grid row track.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-rows)
   */
  gridAutoRows?: ResponsiveValue<CSS.GridAutoRowsProperty<TLength>>
  /**
   * The grid-column CSS property is a shorthand property for grid-column-start and grid-column-end specifying
   * a grid item's size and location within the grid column by contributing a line, a span, or nothing (automatic)
   * to its grid placement, thereby specifying the inline-start and inline-end edge of its grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column)
   */
  gridColumn?: ResponsiveValue<CSS.GridColumnProperty>
  /**
   * The column-gap CSS property sets the size of the gap (gutter) between an element's columns.
   *
   * @deprecated use column-gap
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/column-gap)
   */
  gridColumnGap?: ResponsiveValue<CSS.GridColumnGapProperty<TLength>>
  /**
   * The gap CSS property sets the gaps (gutters) between rows and columns. It is a shorthand for row-gap
   * and column-gap.
   *
   * @deprecated use gap
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/gap)
   */
  gridGap?: ResponsiveValue<CSS.GridGapProperty<TLength>>
  /**
   * The grid-row CSS property is a shorthand property for grid-row-start and grid-row-end specifying a grid item’s
   * size and location within the grid row by contributing a line, a span, or nothing (automatic) to its grid
   * placement, thereby specifying the inline-start and inline-end edge of its grid area.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-row)
   */
  gridRow?: ResponsiveValue<CSS.GridRowProperty>
  /**
   * The row-gap CSS property sets the size of the gap (gutter) between an element's rows.
   *
   * @deprecated use row-gap
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/row-gap)
   */
  gridRowGap?: ResponsiveValue<CSS.GridRowGapProperty<TLength>>
  /**
   * The grid-template-areas CSS property specifies named grid areas.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-areas)
   */
  gridTemplateAreas?: ResponsiveValue<CSS.GridTemplateAreasProperty>
  /**
   * The grid-template-columns CSS property defines the line names and track sizing functions of the grid columns.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns)
   */
  gridTemplateColumns?: ResponsiveValue<
    CSS.GridTemplateColumnsProperty<TLength>
  >
  /**
   * The grid-template-rows CSS property defines the line names and track sizing functions of the grid rows.
   *
   * [MDN reference](https://developer.mozilla.org/en-US/docs/Web/CSS/row-template-rows)
   */
  gridTemplateRows?: ResponsiveValue<CSS.GridTemplateRowsProperty<TLength>>
}

export const config: Config = {
  gridArea: true,
  gridAutoColumns: true,
  gridAutoFlow: true,
  gridAutoRows: true,
  gridColumn: true,
  gridColumnGap: {
    property: 'gridColumnGap',
    scale: 'space'
  },
  gridGap: {
    property: 'gridGap',
    scale: 'space'
  },
  gridRow: true,
  gridRowGap: {
    property: 'gridRowGap',
    scale: 'space'
  },
  gridTemplateAreas: true,
  gridTemplateColumns: true,
  gridTemplateRows: true
}

export const gridProps = system(config)
